import { useState, useEffect, useRef } from 'react';
import { useGet } from '@/api/useApi';
import { useDebounce } from '@/hooks';
import { axiosInstance } from '@/rest-data-provider/utils/axios';

// Once we've paid the cost of discovering a query's real last page, remember
// it for the rest of the session — a reload or navigating back to the same
// list shouldn't have to re-probe from scratch. Keyed on the raw total too,
// so if the backend's count for this query ever changes, the cached answer
// is naturally invalidated instead of served stale.
// Bump this when the resolution algorithm changes, so a previously cached
// answer computed by older (buggier) logic doesn't get served as if it were
// still trustworthy.
const CACHE_VERSION = 'v3';

const resolvedTotalCacheKey = (
  endpoint: string,
  queryParamsKey: string,
  pageSize: number,
  rawTotal: number
) =>
  `dijo-resolved-total:${CACHE_VERSION}:${endpoint}:${queryParamsKey}:${pageSize}:${rawTotal}`;

const readCachedTotal = (key: string): number | undefined => {
  try {
    const value = sessionStorage.getItem(key);
    return value === null ? undefined : Number(value);
  } catch {
    return undefined;
  }
};

const writeCachedTotal = (key: string, total: number): void => {
  try {
    sessionStorage.setItem(key, String(total));
  } catch {
    // sessionStorage unavailable (private browsing, etc.) — safe to skip.
  }
};

interface UseListParams {
  endpoint: string;
  defaultPageSize?: number;
  defaultSearch?: string;
  extraParams?: Record<string, any>;
  filters?: Record<string, any>;
}

export const useList = <T>({
  endpoint,
  defaultPageSize = 10,
  defaultSearch = '',
  extraParams = {},
  filters: initialFilters = {},
}: UseListParams) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(defaultSearch);
  const [name, setName] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const debouncedSearch = useDebounce(search, 500);
  const debouncedName = useDebounce(name, 500);

  const queryParams = {
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(debouncedName && { name: debouncedName }),
    ...filters,
    ...extraParams,
  };

  const { data, isLoading, refetch, isRefetching, isError } = useGet<T>(
    endpoint,
    endpoint,
    {
      params: {
        page,
        page_size: defaultPageSize,
        ...queryParams,
      },
    }
  );

  useEffect(() => {
    refetch();
  }, [debouncedSearch, debouncedName, filters, refetch, page]);

  // The backend's reported total-count can include rows its list query
  // filters out, so it can overstate how many pages actually have data —
  // the pager would then offer pages that come back empty. Rather than let
  // the user discover that by clicking through dead pages, proactively
  // verify every page up to the reported total in the background (one
  // parallel batch of requests, not sequential) and expose a total that's
  // been confirmed to have data, so the pager shows the right page numbers
  // from the start.
  const [resolvedTotal, setResolvedTotal] = useState<number | undefined>(
    undefined
  );
  const probeTokenRef = useRef(0);
  const queryParamsKey = JSON.stringify(queryParams);
  const rawTotalCountHeader = data?.headers?.['x-total-count'];

  useEffect(() => {
    setResolvedTotal(undefined);
  }, [queryParamsKey, defaultPageSize]);

  useEffect(() => {
    const rawTotal =
      typeof rawTotalCountHeader === 'string'
        ? Number(rawTotalCountHeader)
        : rawTotalCountHeader;

    if (
      typeof rawTotal !== 'number' ||
      Number.isNaN(rawTotal) ||
      rawTotal <= 0
    ) {
      return;
    }

    const lastPage = Math.ceil(rawTotal / defaultPageSize);

    if (lastPage <= 1) {
      setResolvedTotal(rawTotal);
      return;
    }

    const cacheKey = resolvedTotalCacheKey(
      endpoint,
      queryParamsKey,
      defaultPageSize,
      rawTotal
    );
    const cached = readCachedTotal(cacheKey);

    if (cached !== undefined) {
      setResolvedTotal(cached);
      return;
    }

    // Deliberately leave resolvedTotal (and thus the pager) unset while the
    // probe below runs, rather than seed it with the raw total — that would
    // just trade one visible glitch (pager briefly shows nothing extra) for
    // another (pager briefly shows a wrong number and corrects itself).
    const token = ++probeTokenRef.current;
    let cancelled = false;

    const extractLength = (response: { data?: { data?: unknown } }): number => {
      const payload = response?.data?.data;

      if (Array.isArray(payload)) {
        return payload.length;
      }

      // Some endpoints nest the list under a named key, e.g.
      // { data: { classes: [...] } } rather than { data: [...] }.
      if (payload && typeof payload === 'object') {
        const nestedArray = Object.values(payload).find((value) =>
          Array.isArray(value)
        );

        if (Array.isArray(nestedArray)) {
          return nestedArray.length;
        }
      }

      return 0;
    };

    const requestPage = (probePage: number) =>
      axiosInstance.get(endpoint, {
        params: {
          page: probePage,
          page_size: defaultPageSize,
          ...queryParams,
        },
      });

    // Some backends reject an out-of-range page with a 4xx instead of
    // returning 200 with an empty list. That's just as decisive a signal
    // that the page has no data as an empty array — but only once we're
    // sure it's really the page's fault and not the backend having a
    // passing bad moment. Retry with backoff before trusting it.
    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 300;
    const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const fetchPageLength = async (probePage: number): Promise<number> => {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          return extractLength(await requestPage(probePage));
        } catch (error) {
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          const isClientError =
            typeof status === 'number' && status >= 400 && status < 500;

          if (!isClientError) {
            throw error;
          }

          if (attempt < MAX_ATTEMPTS) {
            // eslint-disable-next-line no-await-in-loop
            await delay(RETRY_DELAY_MS);
          }
        }
      }

      // Every attempt came back as a client error (out-of-range page) —
      // treat it as confirmed empty.
      return 0;
    };

    // Check every page directly, in parallel, rather than jump around
    // narrowing a search range — a range-narrowing search can permanently
    // lose track of a real populated page if one probe in the middle of a
    // round comes back as a false negative. The true last page is the end
    // of the longest *contiguous* run of populated pages starting from page
    // 1 (a one-off gap can't be represented in a plain 1..N pager anyway).
    // Bounded so this can't fire off hundreds of requests for a huge list.
    const MAX_PROBEABLE_PAGES = 100;

    const resolve = async () => {
      try {
        if (lastPage > MAX_PROBEABLE_PAGES) {
          // Too many pages to exhaustively verify — trust the raw total
          // rather than hammer the backend with requests.
          setResolvedTotal(rawTotal);
          return;
        }

        const pages = Array.from({ length: lastPage }, (_, i) => i + 1);
        const lengths = await Promise.all(
          pages.map((probePage) => fetchPageLength(probePage))
        );

        if (cancelled || probeTokenRef.current !== token) {
          return;
        }

        let lastGoodPage = 0;
        let lastGoodLength = 0;

        for (let i = 0; i < pages.length; i += 1) {
          if (lengths[i] > 0) {
            lastGoodPage = pages[i];
            lastGoodLength = lengths[i];
          } else {
            break;
          }
        }

        const resolved =
          lastGoodPage === 0
            ? 0
            : (lastGoodPage - 1) * defaultPageSize + lastGoodLength;

        setResolvedTotal(resolved);
        writeCachedTotal(cacheKey, resolved);
      } catch {
        if (!cancelled && probeTokenRef.current === token) {
          // Probing failed — fall back to trusting the raw total rather
          // than blocking pagination entirely.
          setResolvedTotal(rawTotal);
        }
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTotalCountHeader, defaultPageSize, endpoint, queryParamsKey]);

  return {
    data,
    isLoading,
    isRefetching,
    isError,
    page,
    search,
    name,
    filters,
    setSearch,
    setName,
    setPage,
    setFilters,
    refetch,
    resolvedTotal,
    isTotalReady: resolvedTotal !== undefined,
  };
};
